/**
 * NoemaGraphView — Force-Directed SVG Graph + Metrics Bar
 *
 * Visualizes the intentional graph: eidetic objects as nodes,
 * noetic acts as edges. Author node at center.
 *
 * @module @humanizer/studio/components/tools/NoemaGraphView
 */

import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type {
  NoemaGraphResult, NoemaObject, NoemaAct, NoemaMetrics,
  NoeticActType, NoeticActCategory,
} from '../../types/analysis';
import { NoemaTrajectoryView } from './NoemaTrajectoryView';
import '../../styles/noema-graph.css';

// -----------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------

const ACT_CATEGORY_MAP: Record<NoeticActType, NoeticActCategory> = {
  asserting: 'constitutive', defining: 'constitutive', establishing: 'constitutive', negating: 'constitutive',
  remembering: 'temporal', anticipating: 'temporal', retaining: 'temporal', protending: 'temporal',
  valuing: 'evaluative', regretting: 'evaluative', desiring: 'evaluative', fearing: 'evaluative',
  questioning: 'epistemic', doubting: 'epistemic', believing: 'epistemic', knowing: 'epistemic', imagining: 'epistemic',
  comparing: 'relational', distinguishing: 'relational', connecting: 'relational', opposing: 'relational',
};

const CATEGORY_COLORS: Record<NoeticActCategory, string> = {
  constitutive: 'var(--noema-constitutive)',
  temporal: 'var(--noema-temporal)',
  evaluative: 'var(--noema-evaluative)',
  epistemic: 'var(--noema-epistemic)',
  relational: 'var(--noema-relational)',
};

const OBJECT_TYPE_COLORS: Record<string, string> = {
  person: 'var(--noema-obj-person)',
  place: 'var(--noema-obj-place)',
  event: 'var(--noema-obj-event)',
  concept: 'var(--noema-obj-concept)',
  sensation: 'var(--noema-obj-sensation)',
  artifact: 'var(--noema-obj-artifact)',
  relation: 'var(--noema-obj-relation)',
  state: 'var(--noema-obj-state)',
};

const AUTHOR_NODE_ID = '_author';

// -----------------------------------------------------------------------
// Simple force-directed layout (no d3 dependency)
// -----------------------------------------------------------------------

interface LayoutNode {
  id: string;
  label: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  color: string;
  isAuthor: boolean;
}

interface LayoutEdge {
  sourceId: string;
  targetId: string;
  color: string;
  actType: NoeticActType;
  evidence: string;
}

function createLayout(objects: NoemaObject[], acts: NoemaAct[], width: number, height: number): {
  nodes: LayoutNode[];
  edges: LayoutEdge[];
} {
  const cx = width / 2;
  const cy = height / 2;

  // Author node at center
  const nodes: LayoutNode[] = [{
    id: AUTHOR_NODE_ID,
    label: 'Author',
    x: cx,
    y: cy,
    vx: 0,
    vy: 0,
    radius: 18,
    color: 'var(--noema-author)',
    isAuthor: true,
  }];

  // Object nodes arranged in a circle initially
  const maxSalience = Math.max(...objects.map(o => o.salience), 0.1);
  objects.forEach((obj, i) => {
    const angle = (i / Math.max(objects.length, 1)) * Math.PI * 2;
    const dist = 80 + Math.random() * 60;
    nodes.push({
      id: obj.id,
      label: obj.label,
      x: cx + Math.cos(angle) * dist,
      y: cy + Math.sin(angle) * dist,
      vx: 0,
      vy: 0,
      radius: 6 + (obj.salience / maxSalience) * 14,
      color: OBJECT_TYPE_COLORS[obj.objectType] || 'var(--noema-obj-concept)',
      isAuthor: false,
    });
  });

  // Edges
  const edges: LayoutEdge[] = acts.map(act => ({
    sourceId: act.sourceObjectId,
    targetId: act.targetObjectId,
    color: CATEGORY_COLORS[ACT_CATEGORY_MAP[act.actType]] || 'var(--noema-constitutive)',
    actType: act.actType,
    evidence: act.evidence,
  }));

  // Run force simulation (simple: repulsion + attraction)
  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  const iterations = 80;
  const repulsion = 2000;
  const attraction = 0.01;
  const damping = 0.85;

  for (let iter = 0; iter < iterations; iter++) {
    // Repulsion between all pairs
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const dx = nodes[j].x - nodes[i].x;
        const dy = nodes[j].y - nodes[i].y;
        const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 1);
        const force = repulsion / (dist * dist);
        const fx = (dx / dist) * force;
        const fy = (dy / dist) * force;
        nodes[i].vx -= fx;
        nodes[i].vy -= fy;
        nodes[j].vx += fx;
        nodes[j].vy += fy;
      }
    }

    // Attraction along edges
    for (const edge of edges) {
      const s = nodeMap.get(edge.sourceId);
      const t = nodeMap.get(edge.targetId);
      if (!s || !t) continue;
      const dx = t.x - s.x;
      const dy = t.y - s.y;
      const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 1);
      const force = dist * attraction;
      const fx = (dx / dist) * force;
      const fy = (dy / dist) * force;
      s.vx += fx;
      s.vy += fy;
      t.vx -= fx;
      t.vy -= fy;
    }

    // Center gravity for author node
    const author = nodeMap.get(AUTHOR_NODE_ID);
    if (author) {
      author.vx += (cx - author.x) * 0.1;
      author.vy += (cy - author.y) * 0.1;
    }

    // Update positions with damping
    for (const n of nodes) {
      n.vx *= damping;
      n.vy *= damping;
      n.x += n.vx;
      n.y += n.vy;
      // Keep within bounds
      n.x = Math.max(n.radius + 10, Math.min(width - n.radius - 10, n.x));
      n.y = Math.max(n.radius + 10, Math.min(height - n.radius - 10, n.y));
    }
  }

  return { nodes, edges };
}

// -----------------------------------------------------------------------
// Metrics bar
// -----------------------------------------------------------------------

function MetricsBar({ metrics }: { metrics: NoemaMetrics }) {
  const items = [
    { label: 'Tension', value: metrics.tension },
    { label: 'Novelty', value: metrics.novelty },
    { label: 'Surprisal', value: metrics.surprisal },
    { label: 'Depth', value: metrics.engagementDepth },
  ];

  return (
    <div className="noema-metrics">
      {items.map(item => (
        <div key={item.label} className="noema-metrics__item">
          <span className="noema-metrics__label">{item.label}</span>
          <div className="noema-metrics__bar">
            <div
              className="noema-metrics__fill"
              style={{ width: `${item.value * 100}%` }}
            />
          </div>
          <span className="noema-metrics__value">{(item.value * 100).toFixed(0)}%</span>
        </div>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------
// Object list
// -----------------------------------------------------------------------

function ObjectList({ objects, selectedId, onSelect }: {
  objects: NoemaObject[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}) {
  const sorted = useMemo(
    () => [...objects].sort((a, b) => b.salience - a.salience),
    [objects]
  );

  return (
    <div className="noema-objects">
      <div className="noema-objects__header">Eidetic Objects ({objects.length})</div>
      <div className="noema-objects__list">
        {sorted.map(obj => (
          <button
            key={obj.id}
            className={`noema-objects__item ${selectedId === obj.id ? 'noema-objects__item--selected' : ''}`}
            onClick={() => onSelect(selectedId === obj.id ? null : obj.id)}
          >
            <span
              className="noema-objects__dot"
              style={{ backgroundColor: OBJECT_TYPE_COLORS[obj.objectType] || 'var(--noema-obj-concept)' }}
            />
            <span className="noema-objects__label">{obj.label}</span>
            <span className="noema-objects__meta">
              {obj.objectType} | {(obj.salience * 100).toFixed(0)}%
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Act distribution
// -----------------------------------------------------------------------

function ActDistribution({ acts }: { acts: NoemaAct[] }) {
  const dist = useMemo(() => {
    const counts: Record<NoeticActCategory, number> = {
      constitutive: 0, temporal: 0, evaluative: 0, epistemic: 0, relational: 0,
    };
    for (const act of acts) {
      counts[ACT_CATEGORY_MAP[act.actType]]++;
    }
    return counts;
  }, [acts]);

  const total = Math.max(Object.values(dist).reduce((s, v) => s + v, 0), 1);

  return (
    <div className="noema-act-dist">
      <div className="noema-act-dist__header">Noetic Acts ({total})</div>
      <div className="noema-act-dist__bars">
        {(Object.entries(dist) as Array<[NoeticActCategory, number]>).map(([cat, count]) => (
          <div key={cat} className="noema-act-dist__row">
            <span className="noema-act-dist__label">{cat}</span>
            <div className="noema-act-dist__bar">
              <div
                className="noema-act-dist__fill"
                style={{
                  width: `${(count / total) * 100}%`,
                  backgroundColor: CATEGORY_COLORS[cat],
                }}
              />
            </div>
            <span className="noema-act-dist__count">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Main graph component
// -----------------------------------------------------------------------

export interface NoemaGraphViewProps {
  data: NoemaGraphResult;
  className?: string;
  compact?: boolean;
}

export function NoemaGraphView({ data, className = '', compact = true }: NoemaGraphViewProps): React.ReactElement {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoveredEdge, setHoveredEdge] = useState<{ actType: string; evidence: string; x: number; y: number } | null>(null);
  const [selectedObject, setSelectedObject] = useState<string | null>(null);
  const [viewTab, setViewTab] = useState<'graph' | 'trajectory'>('graph');
  const [modalOpen, setModalOpen] = useState(false);

  const width = compact ? 500 : 800;
  const height = compact ? 400 : 500;
  const labelMaxLen = compact ? 20 : 40;

  const { nodes, edges } = useMemo(
    () => createLayout(data.objects, data.acts, width, height),
    [data.objects, data.acts, width, height]
  );

  const nodeMap = useMemo(
    () => new Map(nodes.map(n => [n.id, n])),
    [nodes]
  );

  const handleEdgeHover = useCallback((edge: LayoutEdge | null, evt?: React.MouseEvent) => {
    if (edge && evt) {
      setHoveredEdge({
        actType: edge.actType,
        evidence: edge.evidence,
        x: evt.clientX,
        y: evt.clientY,
      });
    } else {
      setHoveredEdge(null);
    }
  }, []);

  // Highlight edges connected to selected object
  const highlightedEdges = useMemo(() => {
    if (!selectedObject) return new Set<number>();
    const set = new Set<number>();
    edges.forEach((e, i) => {
      if (e.sourceId === selectedObject || e.targetId === selectedObject) {
        set.add(i);
      }
    });
    return set;
  }, [selectedObject, edges]);

  const containerClassName = `noema-graph ${compact ? '' : 'noema-graph--expanded'} ${className}`.trim();

  const graphContent = (
    <div className={containerClassName}>
      <MetricsBar metrics={data.metrics} />

      {/* Tab switcher */}
      <div className="noema-graph__tabs">
        <button
          className={`noema-graph__tab ${viewTab === 'graph' ? 'noema-graph__tab--active' : ''}`}
          onClick={() => setViewTab('graph')}
        >
          Graph
        </button>
        <button
          className={`noema-graph__tab ${viewTab === 'trajectory' ? 'noema-graph__tab--active' : ''}`}
          onClick={() => setViewTab('trajectory')}
        >
          Trajectory
        </button>
      </div>

      {viewTab === 'trajectory' && (
        <NoemaTrajectoryView data={data} />
      )}

      {viewTab === 'graph' && <><div className="noema-graph__container">
        {/* Expand button — compact mode only */}
        {compact && (
          <button
            className="noema-graph__expand-btn"
            onClick={() => setModalOpen(true)}
            title="Open in full view"
          >
            Expand
          </button>
        )}

        <svg
          ref={svgRef}
          className="noema-graph__svg"
          viewBox={`0 0 ${width} ${height}`}
          width="100%"
          height={height}
        >
          {/* Edges */}
          {edges.map((edge, i) => {
            const source = nodeMap.get(edge.sourceId);
            const target = nodeMap.get(edge.targetId);
            if (!source || !target) return null;

            const isHighlighted = highlightedEdges.has(i);
            const opacity = selectedObject ? (isHighlighted ? 0.8 : 0.1) : 0.4;

            return (
              <line
                key={i}
                x1={source.x}
                y1={source.y}
                x2={target.x}
                y2={target.y}
                stroke={edge.color}
                strokeWidth={isHighlighted ? 2 : 1}
                strokeOpacity={opacity}
                onMouseEnter={(evt) => handleEdgeHover(edge, evt)}
                onMouseLeave={() => handleEdgeHover(null)}
                className="noema-graph__edge"
              />
            );
          })}

          {/* Nodes */}
          {nodes.map(node => {
            const isSelected = selectedObject === node.id;
            const dimmed = selectedObject && !isSelected && !highlightedEdges.size;

            return (
              <g
                key={node.id}
                className={`noema-graph__node ${node.isAuthor ? 'noema-graph__node--author' : ''}`}
                onClick={() => setSelectedObject(isSelected ? null : node.id)}
                style={{ cursor: 'pointer' }}
              >
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={node.radius}
                  fill={node.color}
                  fillOpacity={dimmed ? 0.3 : 0.85}
                  stroke={isSelected ? 'var(--studio-text)' : 'none'}
                  strokeWidth={isSelected ? 2 : 0}
                />
                {(node.radius >= 10 || node.isAuthor) && (
                  <text
                    x={node.x}
                    y={node.y + node.radius + 12}
                    textAnchor="middle"
                    className="noema-graph__label"
                    fillOpacity={dimmed ? 0.3 : 1}
                  >
                    {node.label.length > labelMaxLen ? node.label.slice(0, labelMaxLen - 2) + '...' : node.label}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {/* Hover tooltip */}
        {hoveredEdge && (
          <div
            className="noema-graph__tooltip"
            style={{
              position: 'fixed',
              left: hoveredEdge.x + 12,
              top: hoveredEdge.y - 20,
            }}
          >
            <strong>{hoveredEdge.actType}</strong>
            {hoveredEdge.evidence && (
              <div className="noema-graph__tooltip-evidence">
                "{hoveredEdge.evidence}"
              </div>
            )}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="noema-graph__legend">
        {(Object.entries(CATEGORY_COLORS) as Array<[NoeticActCategory, string]>).map(([cat, color]) => (
          <span key={cat} className="noema-graph__legend-item">
            <span className="noema-graph__legend-dot" style={{ backgroundColor: color }} />
            {cat}
          </span>
        ))}
      </div>

      <div className="noema-graph__panels">
        <ObjectList
          objects={data.objects}
          selectedId={selectedObject}
          onSelect={setSelectedObject}
        />
        <ActDistribution acts={data.acts} />
      </div>
      </>}

      {/* Processing info */}
      <div className="noema-graph__meta">
        {data.processingInfo.totalSentences} sentences |
        {' '}{data.objects.length} objects |
        {' '}{data.acts.length} acts |
        {' '}{data.processingInfo.llmCallCount} LLM calls |
        {' '}{data.processingInfo.processingTimeMs}ms
      </div>
    </div>
  );

  return (
    <>
      {graphContent}

      {/* Modal portal — renders expanded graph in overlay */}
      {modalOpen && compact && createPortal(
        <div
          className="noema-graph__modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}
        >
          <div className="noema-graph__modal-panel">
            <div className="noema-graph__modal-header">
              <span className="noema-graph__modal-title">Noema Graph</span>
              <button
                className="noema-graph__modal-close"
                onClick={() => setModalOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="noema-graph__modal-body">
              <NoemaGraphView data={data} compact={false} />
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default NoemaGraphView;
